import axios, { AxiosInstance } from 'axios';
import { createContext, useContext, useMemo } from 'react';
import type { InternalAxiosRequestConfig } from 'axios';
import { useAuth } from './authProvider';
import { getPreviewSession } from '@/lib/preview';

type ApiContextValue = {
  instance: AxiosInstance;
};
const ApiContext = createContext<ApiContextValue | null>(null);

export const ApiProvider = (props: { children?: React.ReactNode }) => {
  const { getAccessToken } = useAuth();

  const instance = useMemo(() => {
    const instance = axios.create({
      baseURL: `${import.meta.env.VITE_API_URL}/api`,
    });
    instance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        const token = await getAccessToken();
        config.headers.set('Authorization', `Bearer ${token}`);
        const preview = getPreviewSession();
        if (preview) {
          config.headers.set('X-Impersonate-User-Id', preview.impersonateUserId);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
    return instance;
  }, [getAccessToken]);

  return <ApiContext.Provider {...props} value={{ instance }} />;
};

export const useApi = () => {
  const context = useContext(ApiContext);

  if (!context) {
    throw new Error('API is not provided');
  }
  return context.instance;
};
